import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CHANGELOG_EVENT } from './ChangelogModal';
import styled from 'styled-components';

type SWUpdateDetail = { registration: ServiceWorkerRegistration };

type VersionInfo = {
  version: string;
  date?: string;
  changes?: string[];
};

const Bar = styled.div`
  position: fixed; left:0; right:0; bottom:0; z-index:9999; display:flex; flex-direction:column; gap:8px;
  padding: 14px clamp(12px,3vw,28px) calc(10px + env(safe-area-inset-bottom,0));
  background: linear-gradient(135deg, rgba(255,255,255,0.18), rgba(255,255,255,0.1)) , var(--theme-card-background, rgba(22,22,30,0.92));
  color: var(--theme-text,#f5f5f5); backdrop-filter: blur(14px) saturate(180%);
  box-shadow: 0 -6px 24px -4px rgba(0,0,0,0.55), 0 -2px 8px rgba(0,0,0,0.4);
  border-top: 1px solid rgba(255,255,255,0.25);
  
  /* Förhindra horizontal scrolling när notification visas */
  max-width: 100vw;
  overflow-x: hidden;
`;
const Row = styled.div`
  display:flex; align-items:center; justify-content:space-between; gap:12px; flex-wrap:wrap;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
  }
`;
const Btn = styled.button<{ $variant?: 'primary' | 'success' | 'neutral' }>`
  appearance:none; border:0; border-radius:10px; padding:10px 16px; font-weight:600; cursor:pointer; font-size:14px;
  background:${p=>p.$variant==='success'
    ? 'linear-gradient(135deg,#16a34a,#15803d)'
    : p.$variant==='neutral'
      ? 'linear-gradient(135deg,#4b5563,#374151)'
      : 'linear-gradient(135deg,var(--theme-primary,#2563eb),var(--theme-secondary,#1d4ed8))'};
  color:#fff; box-shadow:0 4px 14px -3px rgba(0,0,0,0.55);
  display:inline-flex; align-items:center; gap:6px; letter-spacing:.3px; transition:transform .25s ease, box-shadow .25s ease, filter .25s ease;
  flex: 1;
  min-width: 0; /* Allow shrinking */
  
  &:hover{transform:translateY(-3px); box-shadow:0 8px 22px -5px rgba(0,0,0,0.6);} 
  &:active{transform:translateY(-1px) scale(.96);}
  
  @media (max-width: 768px) {
    padding: 12px 16px;
    font-size: 15px;
    min-height: 44px; /* iOS minimum touch target */
  }
`;
const TitleTxt = styled.div`font-weight:700; font-size:15px;`;
const SubTxt = styled.div`opacity:.9; font-size:13px;`;
const ChangesList = styled.ul`margin:6px 0 0 18px; padding:0; font-size:13px; line-height:1.35;`;
const ErrorTxt = styled.div`color:#fecaca; font-size:13px;`;

export default function UpdateNotifier() {
  const [open, setOpen] = useState(false);
  const [info, setInfo] = useState<VersionInfo | null>(null);
  const registrationRef = useRef<ServiceWorkerRegistration | null>(null);
  const [error, setError] = useState<string | null>(null);
  const checkIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const openRef = useRef(false);

  useEffect(() => {
    openRef.current = open;
  }, [open]);

  const reloadNow = () => {
    const reg = registrationRef.current;
    const waiting = reg?.waiting;
    try {
      if (waiting) {
        waiting.postMessage('SKIP_WAITING');
      } else if (reg?.installing) {
        reg.installing.postMessage?.('SKIP_WAITING');
      }
    } catch {}

    let reloaded = false;
    const onControllerChange = () => {
      if (!reloaded) {
        reloaded = true;
        navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
        clearTimeout(timeoutId);
        window.location.reload();
      }
    };
    navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);
    // Förbättrad cleanup med removeEventListener
    const timeoutId = window.setTimeout(() => {
      if (!reloaded) {
        navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
        window.location.reload();
      }
    }, 3000);
  };

  const fetchVersionInfo = async (retries = 3) => {
    for (let i = 0; i < retries; i++) {
      try {
        const res = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = (await res.json()) as VersionInfo;
        setInfo(json);
        setError(null);
        return;
      } catch (e: any) {
        if (i === retries - 1) {
          setError(e?.message || 'Kunde inte hämta ändringslogg');
        } else {
          // Wait before retry
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }
    }
  };

  useEffect(() => {
    const handler = (ev: Event) => {
      const detail = (ev as CustomEvent<SWUpdateDetail>).detail;
      registrationRef.current = detail.registration;
      console.log('[UpdateNotifier] SW update detected, showing notification');
      setOpen(true);
      fetchVersionInfo();
    };
    
    // Kontrollera för uppdateringar periodiskt
    const checkForUpdates = async () => {
      if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        try {
          const reg = await navigator.serviceWorker.getRegistration();
          if (reg) {
            await reg.update(); // Tvinga uppdateringskontroll
            if (reg.waiting && !openRef.current) {
              console.log('[UpdateNotifier] Update found via periodic check');
              registrationRef.current = reg;
              setOpen(true);
              fetchVersionInfo();
            }
          }
        } catch (e) {
          console.error('[UpdateNotifier] Error checking for updates:', e);
        }
      }
    };
    
    // Listen for app focus to potentially re-check for updates
    const focusHandler = () => {
      checkForUpdates();
    };
    
    // Kontrollera var 30:e sekund i development, var 5:e minut i production
    const interval = process.env.NODE_ENV === 'development' ? 30000 : 300000;
    checkIntervalRef.current = setInterval(checkForUpdates, interval);
    
    // Initial check efter 10 sekunder
    setTimeout(checkForUpdates, 10000);
    
    window.addEventListener('app-sw-update', handler as EventListener);
    window.addEventListener('focus', focusHandler);
    
    return () => {
      window.removeEventListener('app-sw-update', handler as EventListener);
      window.removeEventListener('focus', focusHandler);
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
    };
  }, []);

  const title = useMemo(() => {
    if (!info) return 'Ny version tillgänglig!';
    return `Version ${info.version} tillgänglig!`;
  }, [info]);

  if (!open) return null;

  return (
    <Bar>
      <Row>
        <div>
          <TitleTxt>{title}</TitleTxt>
          <SubTxt>Klicka "Uppdatera" för att ladda den senaste versionen</SubTxt>
          {error && <ErrorTxt>{error}</ErrorTxt>}
          {info && info.changes && (
            <ChangesList>
              {info.changes.slice(0, 5).map((c, i) => (<li key={i}>{c}</li>))}
            </ChangesList>
          )}
        </div>
      </Row>
      <Row>
        <Btn $variant="success" onClick={reloadNow}>
          ✨ Uppdatera nu
        </Btn>
        <Btn $variant="neutral" onClick={() => setOpen(false)}>
          ✖ Senare
        </Btn>
        <Btn onClick={() => {
          setOpen(false);
          window.dispatchEvent(new CustomEvent(CHANGELOG_EVENT, { detail: info }));
        }}>
          📋 Visa ändringar
        </Btn>
      </Row>
    </Bar>
  );
}
